import createClient, { type Middleware } from 'openapi-fetch'
import { runtimeConfig } from '@/shared/config'

// Общий HTTP-клиент. Cookie-auth: credentials:'include' гонит httpOnly-cookie
// сессии (Domain=.stit.tech, 00-architecture § 3) в каждый запрос на /api. CSRF не
// нужен — защита mutating-запросов держится на SameSite=Lax + same-site мутации.
// Типы paths появляются после `pnpm gen:api` (Hey API). До этого — осознанно
// untyped: any (не never), иначе параметры запросов схлопываются в unassignable.
// biome-ignore lint/suspicious/noExplicitAny: временно до codegen
type Paths = any // eslint-disable-line @typescript-eslint/no-explicit-any

// Событие «сессия истекла/отсутствует»: любой 401, переживший продление. Слушает
// AuthGate в __root и уводит на единый вход хаба.
export const UNAUTHORIZED_EVENT = 'api:unauthorized'

const authMiddleware: Middleware = {
  onResponse({ response }) {
    if (response.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    return response
  },
}

// Контуров сессии два и они независимы (00-architecture § 3): сотрудник живёт на
// employee-backend (id.stit.tech, cookie stit_session), заказчик — на
// company-backend (api.hub.stit.tech, cookie stit_customer). Обе cookie httpOnly:
// из JS не видно ни срока access, ни наличия refresh — «протухла минуту назад» от
// «сессии нет» отличает только поход в refresh-ручку.
export type SessionKind = 'employee' | 'customer'

// Последний контур, который назвал /api/auth/me. Переживает перезагрузку — иначе
// каждый холодный старт стучится в оба issuer'а. Это подсказка «куда идти первым»,
// а не аутентификация: решает всё равно cookie.
const KIND_KEY = 'auth:kind'

function readStoredKind(): SessionKind | null {
  try {
    const raw = localStorage.getItem(KIND_KEY)
    return raw === 'employee' || raw === 'customer' ? raw : null
  } catch {
    // Приватный режим/заблокированное хранилище — работаем без подсказки.
    return null
  }
}

let lastKind: SessionKind | null = readStoredKind()

export function rememberSessionKind(kind: SessionKind | null): void {
  lastKind = kind
  try {
    if (kind) localStorage.setItem(KIND_KEY, kind)
    else localStorage.removeItem(KIND_KEY)
  } catch {
    // см. readStoredKind — контур останется в памяти вкладки
  }
}

export function lastSessionKind(): SessionKind | null {
  return lastKind
}

// Сессия была и продлить её не вышло — это истечение, а не «первый заход». Разницу
// видно только здесь, а от неё зависит плашка на едином экране входа (?notice=).
let expired = false

export function sessionExpired(): boolean {
  return expired
}

function refreshUrl(kind: SessionKind): string {
  return kind === 'customer'
    ? `${runtimeConfig.HUB_API_BASE_URL}/auth/customer/refresh`
    : `${runtimeConfig.ID_BASE_URL}/auth/refresh`
}

// Ходим мимо apiClient: 401 самого refresh ушёл бы на второй круг.
function refreshOne(kind: SessionKind): Promise<boolean> {
  return fetch(refreshUrl(kind), { method: 'POST', credentials: 'include' })
    .then((res) => res.ok)
    .catch(() => false)
}

// Порядок попыток задаёт последний известный контур; второй пробуем следом —
// подсказка могла протухнуть (сотрудник вошёл в том же браузере, что и заказчик),
// и она не должна выбрасывать живого пользователя. Employee первым на холодном
// старте: он частотнее и главнее при обеих cookie (приоритет из hubkit/sso).
async function runRefresh(): Promise<boolean> {
  const order: SessionKind[] =
    lastKind === 'customer' ? ['customer', 'employee'] : ['employee', 'customer']
  for (const kind of order) {
    if (await refreshOne(kind)) {
      rememberSessionKind(kind)
      expired = false
      return true
    }
  }
  if (lastKind) expired = true
  return false
}

let refreshInFlight: Promise<boolean> | null = null

export function refreshSession(): Promise<boolean> {
  // Refresh ротирующий: параллельные попытки погасили бы друг друга и выбросили
  // живого пользователя, поэтому все 401, пришедшие одновременно, ждут один и тот
  // же промис — независимо от того, какой контур он в итоге продлит.
  refreshInFlight ??= runRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

// Единственная точка врезки продления: openapi-fetch зовёт этот fetch уже собранным
// Request'ом, так что повтор идёт тем же методом, путём, телом и заголовками — и
// одинаково для ручных вызовов, и для сгенерированных. Тело Request одноразовое,
// клон снимаем до отправки. Попытка ровно одна: 401 сразу после успешного refresh —
// это конец сессии, и наверх уходит он (middleware поднимет UNAUTHORIZED_EVENT).
async function fetchWithRefresh(request: Request, init?: RequestInit): Promise<Response> {
  const retry = request.clone()
  const res = await fetch(request, init)
  if (res.status !== 401 || !(await refreshSession())) return res
  return fetch(retry, init)
}

export const apiClient = createClient<Paths>({
  baseUrl: runtimeConfig.API_BASE_URL,
  credentials: 'include',
  fetch: fetchWithRefresh,
})

apiClient.use(authMiddleware)
